import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCapabilities } from '../context/CapabilitiesContext';
import {
  Home,
  Stethoscope,
  UserRound,
  Bot,
  HelpCircle,
  Lock,
  CheckCircle2,
  AlertTriangle,
  PanelLeftClose,
  PanelLeftOpen,
  Activity,
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { hasConsent } = useAuth();
  const { loading: capabilitiesLoading, error: capabilitiesError, isModuleEnabled } = useCapabilities();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Navigation items
  const navItems = [
    { id: 'home', path: '/home', icon: Home, label: 'Home', protected: false },
    { id: 'doctor', path: '/doctor', icon: Stethoscope, label: 'Doctor Simulator', protected: true, moduleKey: 'module_1' },
    { id: 'patient', path: '/patient', icon: UserRound, label: 'Patient Simulator', protected: true, moduleKey: 'module_2', comingSoon: true },
    { id: 'copilot', path: '/copilot', icon: Bot, label: 'Medical Copilot', protected: true, moduleKey: 'module_3', comingSoon: true },
    { id: 'help', path: '/help', icon: HelpCircle, label: 'Help', protected: false },
  ];

  const getItemState = (item) => {
    const comingSoon = Boolean(item.comingSoon);
    const capabilityLoading =
      Boolean(item.moduleKey) && !comingSoon && capabilitiesLoading;
    const capabilityUnavailable =
      Boolean(item.moduleKey) &&
      !comingSoon &&
      !capabilityLoading &&
      (capabilitiesError || !isModuleEnabled(item.moduleKey));
    const consentRestricted =
      item.protected && !comingSoon && !capabilityUnavailable && !hasConsent;

    return {
      comingSoon,
      capabilityLoading,
      capabilityUnavailable,
      consentRestricted,
      disabled: comingSoon || capabilityLoading || capabilityUnavailable || consentRestricted,
    };
  };

  const handleNavigation = (item) => {
    const state = getItemState(item);
    if (state.disabled) {
      return;
    }

    navigate(item.path);
  };

  const isCurrentPath = (path) => {
    if (path === '/doctor') {
      return location.pathname === '/doctor' || location.pathname.startsWith('/doctor/session/');
    }
    return location.pathname === path;
  };

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="logo-section">
            <img
              src="/ApolloLogo.png"
              alt="Apollo Medical AI"
              className="sidebar-logo"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="sidebar-logo-fallback" style={{ display: 'none' }}>
              <Activity size={20} />
            </div>
            {sidebarOpen && <span className="logo-text">Apollo Medical AI</span>}
          </div>
          <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
            {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const state = getItemState(item);
            const statusLabel = state.comingSoon ? 'Coming Soon' : null;

            return (
              <button
                key={item.id}
                className={`nav-item ${isCurrentPath(item.path) ? 'active' : ''} ${
                  state.disabled ? 'disabled' : ''
                } ${state.comingSoon ? 'coming-soon' : ''}`}
                onClick={() => handleNavigation(item)}
                disabled={state.disabled}
                title={!sidebarOpen && statusLabel ? `${item.label}: ${statusLabel}` : item.label}
              >
                <span className="nav-icon">
                  <Icon size={18} />
                </span>
                {sidebarOpen && (
                  <div className="nav-content">
                    <span className="nav-label">{item.label}</span>
                    {statusLabel && (
                      <span className="nav-soon-badge">{statusLabel}</span>
                    )}
                    {!statusLabel && state.consentRestricted && (
                      <span className="nav-lock">
                        <Lock size={14} />
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {sidebarOpen && (
          <div className="sidebar-footer">
            <div className="consent-status">
              {hasConsent ? (
                <div className="consent-granted">
                  <span className="status-icon">
                    <CheckCircle2 size={14} />
                  </span>
                  <span className="status-text">Consent Granted</span>
                </div>
              ) : (
                <div className="consent-required">
                  <span className="status-icon">
                    <AlertTriangle size={14} />
                  </span>
                  <span className="status-text">Consent Required</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? 'with-sidebar' : 'full-width'}`}>
        {children}
      </div>
    </div>
  );
};

export default Layout;
